/**
 * Passnummer har ingen gemensam kontrollsiffra i EU – kontrollsiffrorna sitter i
 * den maskinläsbara raden, inte i det tryckta numret. Länderna byter dessutom
 * format med jämna mellanrum (svenska pass har både 8 siffror och nyare
 * varianter med bokstäver). Därför blockerar vi aldrig på landsformat: avviker
 * numret från det vanligaste mönstret visar vi bara en mjuk upplysning.
 */
export type PassportLocale = "sv" | "en" | "es";

type Rule = { patterns: RegExp[]; example: string };

/** Nycklarna är landsnamnen som används i nationalitetslistan (svenska). */
const RULES: Record<string, Rule> = {
  Sverige: { patterns: [/^\d{8}$/, /^[A-Z]{2}\d{7}$/], example: "12345678 eller AA1234567" },
  Spanien: {
    patterns: [/^[A-Z]{2,3}\d{6}$/, /^[A-Z]{3}\d{6}[A-Z]?$/],
    example: "XDA123456",
  },
  Storbritannien: { patterns: [/^\d{9}$/, /^[A-Z0-9]{9}$/], example: "123456789" },
  Tyskland: { patterns: [/^[CFGHJKLMNPRTVWXYZ0-9]{9}$/, /^[A-Z0-9]{9,10}$/], example: "C01X00T47" },
  Frankrike: { patterns: [/^\d{2}[A-Z]{2}\d{5}$/, /^[A-Z0-9]{9}$/], example: "12AB34567" },
  Danmark: { patterns: [/^\d{9}$/, /^\d{8}$/, /^[A-Z]{2}\d{7}$/], example: "123456789" },
  Norge: { patterns: [/^\d{8}$/, /^[A-Z]{2}\d{7}$/], example: "12345678" },
  Finland: { patterns: [/^[A-Z]{2}\d{7}$/, /^[A-Z]{1,2}\d{6,7}$/], example: "AB1234567" },
  Nederländerna: { patterns: [/^[A-Z]{2}[A-Z0-9]{6}\d$/, /^[A-Z]{1,2}[A-Z0-9]{6,8}$/], example: "AB1234567" },
  Italien: { patterns: [/^[A-Z]{2}\d{7}$/, /^[A-Z]{2}\d{5}[A-Z]{2}$/], example: "YA1234567" },
  Polen: { patterns: [/^[A-Z]{2}\d{7}$/], example: "AB1234567" },
  Irland: { patterns: [/^[A-Z]{2}\d{7}$/, /^[A-Z]{2}\d{6}$/], example: "PA1234567" },
  Belgien: { patterns: [/^[A-Z]{2}\d{6}$/, /^[A-Z]{2}\d{7}$/], example: "EM123456" },
  Portugal: { patterns: [/^[A-Z]{1,2}\d{6}$/, /^[A-Z]{2}\d{6}$/], example: "P123456" },
  Österrike: { patterns: [/^[A-Z]\d{7}$/, /^[A-Z]{1,2}\d{6,7}$/], example: "P1234567" },
  Schweiz: { patterns: [/^[A-Z]\d{7}$/, /^[A-Z]{1,2}\d{6,7}$/], example: "X1234567" },
  USA: { patterns: [/^[A-Z0-9]{6,9}$/], example: "123456789" },
};

/** Versaliserar och tar bort mellanslag och bindestreck. */
export function formatPassportNumber(input: string): string {
  return input.replace(/[\s-]/g, "").toUpperCase().slice(0, 20);
}

export type PassportCheck = { valid: boolean; error?: string; hint?: string };

export function validatePassportNumber(
  country: string,
  input: string,
  locale: PassportLocale = "sv",
): PassportCheck {
  const value = formatPassportNumber(input);
  if (!value) return { valid: true };

  // Hårt fel bara vid uppenbart orimliga värden.
  if (!/^[A-Z0-9]+$/.test(value)) {
    return {
      valid: false,
      error:
        locale === "en"
          ? "A passport number may only contain letters and digits."
          : locale === "es"
            ? "Un número de pasaporte solo puede contener letras y cifras."
            : "Ett passnummer får bara innehålla bokstäver och siffror.",
    };
  }
  if (value.length < 5 || value.length > 15) {
    return {
      valid: false,
      error:
        locale === "en"
          ? "A passport number is normally 5–15 letters and digits."
          : locale === "es"
            ? "Un número de pasaporte suele tener entre 5 y 15 letras y cifras."
            : "Ett passnummer är normalt 5–15 bokstäver och siffror.",
    };
  }

  const rule = RULES[country];
  if (rule && !rule.patterns.some((p) => p.test(value))) {
    return {
      valid: true,
      hint:
        locale === "en"
          ? `Does this look right? Passports from ${country} usually look like ${rule.example}.`
          : locale === "es"
            ? `¿Es correcto? Los pasaportes de ${country} suelen tener el formato ${rule.example}.`
            : `Ser detta rätt ut? Pass från ${country} har oftast formatet ${rule.example}.`,
    };
  }

  return { valid: true };
}
